import type { ErrorMessages, ResultOf } from '@horof/shared';
import { counted, dirhams, FINANCE_ERRORS } from '@horof/shared';
import { financeContext } from './data';
import { graphql } from './gql';

/**
 * Bank statements (arqam doc 57): imported lines matched one by one to the
 * operations and transfers, then the statement closed. Every figure
 * (difference, counts, suggestions) is arqam's.
 */

/** Statements and their lines are refreshed with the finances. */
export const bankContext = {
  additionalTypenames: [
    ...financeContext.additionalTypenames,
    'BankStatement',
    'BankStatementLine',
  ],
};

export const StatementsDocument = graphql(`
  query DeskStatements {
    bankStatements(orderBy: [PERIOD_END_DESC]) {
      nodes {
        id
        accountId
        account {
          name
        }
        periodStart
        periodEnd
        openingBalance
        closingBalance
        difference
        matchedCount
        unmatchedCount
        status
      }
    }
  }
`);

export type StatementRow = NonNullable<
  NonNullable<
    ResultOf<typeof StatementsDocument>['bankStatements']
  >['nodes'][number]
>;

export const StatementDocument = graphql(`
  query DeskStatement($id: UUID!) {
    bankStatement(id: $id) {
      id
      accountId
      account {
        id
        name
      }
      periodStart
      periodEnd
      openingBalance
      closingBalance
      difference
      matchedCount
      unmatchedCount
      status
      documentPhoto {
        id
        url
        contentType
      }
      lines(orderBy: [LINE_NO_ASC]) {
        nodes {
          id
          lineNo
          date
          label
          amount
          balance
          reference
          ignored
          ignoreReason
          matchedOperationId
          matchedTransferId
          matchedOperation {
            id
            date
            description
            category {
              name
            }
            project {
              name
            }
          }
          matchedTransfer {
            id
            date
            description
            fromAccount {
              name
            }
            toAccount {
              name
            }
          }
        }
      }
    }
  }
`);

export type Statement = NonNullable<
  ResultOf<typeof StatementDocument>['bankStatement']
>;
export type Line = NonNullable<Statement['lines']['nodes'][number]>;

export const SuggestionsDocument = graphql(`
  query DeskLineSuggestions($id: UUID!) {
    bankStatementLine(id: $id) {
      id
      suggestedMatches {
        nodes {
          kind
          id
          date
          amount
          description
          daysApart
        }
      }
    }
  }
`);

/** Where a line stands. */
export const lineState = (l: Line): 'matched' | 'ignored' | 'open' =>
  l.matchedOperationId || l.matchedTransferId
    ? 'matched'
    : l.ignored
      ? 'ignored'
      : 'open';

/** The last statement of an account: the next one starts after it. */
export const LastStatementDocument = graphql(`
  query DeskLastStatement($accountId: UUID!) {
    bankStatements(
      condition: { accountId: $accountId }
      orderBy: [PERIOD_END_DESC]
      first: 1
    ) {
      nodes {
        id
        periodEnd
        closingBalance
      }
    }
  }
`);

export const ImportStatementDocument = graphql(`
  mutation DeskImportStatement(
    $accountId: UUID!
    $periodStart: Date!
    $periodEnd: Date!
    $openingBalance: BigFloat!
    $closingBalance: BigFloat!
    $lines: JSON!
    $dryRun: Boolean
    $documentPhotoId: UUID
  ) {
    appImportBankStatement(
      input: {
        accountId: $accountId
        periodStart: $periodStart
        periodEnd: $periodEnd
        openingBalance: $openingBalance
        closingBalance: $closingBalance
        lines: $lines
        dryRun: $dryRun
        documentPhotoId: $documentPhotoId
      }
    ) {
      result {
        statementId
        lineCount
        duplicateLines
        total
      }
    }
  }
`);

export const MatchLineDocument = graphql(`
  mutation DeskMatchLine($lineId: UUID!, $operationId: UUID, $transferId: UUID) {
    appMatchStatementLine(
      input: {
        lineId: $lineId
        operationId: $operationId
        transferId: $transferId
      }
    ) {
      result {
        id
        matchedOperationId
        matchedTransferId
      }
    }
  }
`);

export const UnmatchLineDocument = graphql(`
  mutation DeskUnmatchLine($lineId: UUID!) {
    appUnmatchStatementLine(input: { lineId: $lineId }) {
      result {
        id
        matchedOperationId
        matchedTransferId
      }
    }
  }
`);

export const OperationFromLineDocument = graphql(`
  mutation DeskOperationFromLine(
    $lineId: UUID!
    $projectId: UUID!
    $categoryId: UUID
    $counterpartyId: UUID
    $description: String
  ) {
    appCreateOperationFromLine(
      input: {
        lineId: $lineId
        projectId: $projectId
        categoryId: $categoryId
        counterpartyId: $counterpartyId
        description: $description
      }
    ) {
      result {
        id
      }
    }
  }
`);

export const TransferFromLineDocument = graphql(`
  mutation DeskTransferFromLine(
    $lineId: UUID!
    $otherAccountId: UUID!
    $description: String
  ) {
    appCreateTransferFromLine(
      input: {
        lineId: $lineId
        otherAccountId: $otherAccountId
        description: $description
      }
    ) {
      result {
        id
      }
    }
  }
`);

export const IgnoreLineDocument = graphql(`
  mutation DeskIgnoreLine($lineId: UUID!, $ignored: Boolean!, $reason: String) {
    appIgnoreStatementLine(
      input: { lineId: $lineId, ignored: $ignored, reason: $reason }
    ) {
      result {
        id
        ignored
        ignoreReason
      }
    }
  }
`);

export const CloseStatementDocument = graphql(`
  mutation DeskCloseStatement($id: UUID!) {
    appCloseBankStatement(input: { statementId: $id }) {
      result {
        id
        status
      }
    }
  }
`);

export const ReopenStatementDocument = graphql(`
  mutation DeskReopenStatement($id: UUID!) {
    appReopenBankStatement(input: { statementId: $id }) {
      result {
        id
        status
      }
    }
  }
`);

export const DeleteStatementDocument = graphql(`
  mutation DeskDeleteStatement($id: UUID!) {
    deleteBankStatement(input: { id: $id }) {
      bankStatement {
        id
      }
    }
  }
`);

const lines = (v: unknown) =>
  Array.isArray(v) ? v.join('، ') : String(v ?? '');

/** arqam's bank codes in Arabic (doc 57). */
export const BANK_ERRORS: ErrorMessages = {
  ...FINANCE_ERRORS,
  ACCOUNT_NOT_FOUND: 'الحساب غير موجود.',
  STATEMENT_PERIOD_INVALID: 'بداية الفترة بعد نهايتها.',
  STATEMENT_LINES_REQUIRED: 'لا أسطر في الكشف.',
  STATEMENT_LINE_INVALID: ({ line }) =>
    `السطر ${line}: يلزم تاريخ وبيان ومبلغ غير صفر.`,
  LINE_OUTSIDE_PERIOD: ({ line, date }) =>
    `السطر ${line} بتاريخ ${date} خارج فترة الكشف.`,
  STATEMENT_DOESNT_ADD_UP: ({ difference, linesTotal }) =>
    `الرصيد الافتتاحي + مجموع الأسطر (${dirhams(String(linesTotal))}) لا يساوي الرصيد الختامي: الفرق ${dirhams(String(difference))}. تحقق من الأرصدة أو من أسطر ناقصة.`,
  STATEMENT_LINES_DUPLICATED: ({ lines: l }) =>
    `أسطر مستوردة من قبل: ${lines(l)}. استبعدها أو احذف الكشف القديم.`,
  STATEMENT_NOT_FOUND: 'الكشف غير موجود.',
  STATEMENT_LINE_NOT_FOUND: 'السطر غير موجود. حدّث الصفحة.',
  TRANSFER_NOT_FOUND: 'التحويل غير موجود.',
  STATEMENT_CLOSED: 'الكشف مغلق: أعد فتحه أولاً.',
  MATCH_TARGET_REQUIRED: 'اختر عملية أو تحويلاً.',
  MATCH_WRONG_ACCOUNT: 'هذه العملية ليست على حساب الكشف.',
  MATCH_WRONG_AMOUNT: ({ lineAmount, amount }) =>
    `المبلغ لا يطابق: السطر ${dirhams(String(lineAmount))} والعملية ${dirhams(String(amount))}.`,
  MATCH_ALREADY_USED: 'هذه العملية مطابقة لسطر آخر.',
  LINE_IGNORED: 'السطر متجاهَل: ألغِ التجاهل أولاً.',
  LINE_MATCHED: 'السطر مطابق: فكّ المطابقة أولاً.',
  IGNORE_REASON_REQUIRED: 'اكتب سبب التجاهل.',
  STATEMENT_NOT_MATCHED: ({ unmatched, difference }) =>
    Number(unmatched) > 0
      ? `بقي ${linesCount(Number(unmatched))} بلا مطابقة.`
      : `الفرق مع الحسابات ليس صفراً: ${dirhams(String(difference))}.`,
};

/** «سطر واحد», «3 أسطر», «12 سطراً». */
export const linesCount = (n: number) =>
  counted(n, {
    one: 'سطر واحد',
    two: 'سطران',
    few: 'أسطر',
    many: 'سطراً',
    hundred: 'سطر',
  });
